import { TextColumns } from "@phosphor-icons/react";

import { SplitButton } from "common/components/SplitButton";
import {
  DropdownMenu,
  DropdownMenuCheckbox,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FilterButton from "common/components/ui/FilterButton";
import { typedObjectEntries } from "common/helpers/utils";

import type { Icon } from "@phosphor-icons/react";
import type { VisibilityState } from "@tanstack/react-table";

export interface VisibilityColumn {
  label: string;
  icon?: Icon;
}

interface ColumnsVisibilityControlProps {
  /** Keyed by column id, in the order they should be listed. */
  columns: Record<string, VisibilityColumn>;
  columnVisibility: VisibilityState;
  setColumnVisibility: (columnVisibility: VisibilityState) => void;
  onAddColumn?: () => void;
}

/**
 * Table-instance free columns filter. Only needs the hidable columns and the
 * visibility state, so it can be rendered anywhere that state is available.
 */
export default function ColumnsVisibilityControl({
  columns,
  columnVisibility,
  setColumnVisibility,
  onAddColumn,
}: ColumnsVisibilityControlProps) {
  const commonButtonProps = {
    leftIcon: <TextColumns />,
    size: "sm",
    className: "ml-auto",
    children: "Columns",
  } as const;

  function toggleColumnVisibility(columnId: string) {
    setColumnVisibility({
      ...columnVisibility,
      [columnId]: !columnVisibility[columnId],
    });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {onAddColumn ? (
          <SplitButton
            {...commonButtonProps}
            onSplitButtonClick={onAddColumn}
            splitButtonAriaLabel="Add column"
            variant="outline-black"
          />
        ) : (
          <FilterButton {...commonButtonProps} />
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" size="sm">
        <DropdownMenuList isScrollable={false}>
          {typedObjectEntries(columns).map(([id, { label, icon: Icon }]) => (
            <DropdownMenuItem
              key={id}
              className="group/checkbox"
              onSelect={(e) => {
                e.preventDefault();
                toggleColumnVisibility(id);
              }}
            >
              <DropdownMenuCheckbox
                isChecked={columnVisibility[id]}
                onCheckedChange={() => toggleColumnVisibility(id)}
              />

              {Icon && <Icon weight="fill" />}
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
